import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { PaperPlaneTilt, CircleNotch, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../../context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(c.success);
    } catch (err) {
      toast.error(err?.response?.status === 429 ? c.tooMany : c.error);
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-[#0F172A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition";

  return (
    <section id="contatti" className="bg-[#0A0F1A] py-24 lg:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-mono-tech text-xs uppercase tracking-[0.2em] font-semibold text-emerald-500">
            [ {c.overline} ]
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 font-display font-bold text-4xl sm:text-5xl tracking-tight text-white">
            {c.title}
          </motion.h2>
          <p className="mt-5 max-w-md text-slate-400 leading-relaxed">{c.subtitle}</p>
          <div className="mt-8 flex items-center gap-3 text-sm text-slate-300">
            <span className="flex h-10 w-10 items-center justify-center rounded-[4px] bg-emerald-500/15 text-emerald-400"><EnvelopeSimple size={20} /></span>
            certificoqr@aldebran.com
          </div>
        </div>

        {sent ? (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-10 text-center" data-testid="contact-success">
            <CheckCircle size={44} weight="fill" className="mx-auto text-emerald-400" />
            <h3 className="mt-5 font-display font-bold text-xl text-white">{c.sentTitle}</h3>
            <p className="mt-2 text-sm text-slate-400">{c.sentSub}</p>
          </motion.div>
        ) : (
          <motion.form onSubmit={submit} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-md border border-white/10 bg-white/[0.03] p-7 space-y-4" data-testid="contact-form">
            <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
              tabIndex={-1} autoComplete="off" aria-hidden="true"
              className="absolute -left-[9999px] h-0 w-0 opacity-0" name="website" />
            <div>
              <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">{c.nameLabel}</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={120}
                placeholder={c.namePlaceholder} className={input} data-testid="contact-name-input" />
            </div>
            <div>
              <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} maxLength={200}
                placeholder="nome@azienda.it" className={input} data-testid="contact-email-input" />
            </div>
            <div>
              <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">{c.messageLabel}</label>
              <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} maxLength={3000}
                placeholder={c.messagePlaceholder} className={`${input} resize-none`} data-testid="contact-message-input" />
            </div>
            <button type="submit" disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-3.5 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors disabled:opacity-60"
              data-testid="contact-submit-btn">
              {busy ? <CircleNotch size={17} className="animate-spin" /> : <PaperPlaneTilt size={17} weight="bold" />} {c.cta}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
};
